import { memberWorkAreas } from '@featit/shared/guest-work-area';
import { type IProject } from '@featit/shared/types/index';
import { APP_HOME_PATH } from '@/routing/appHome';
import { WORK_AREA_SEED_IDS } from '@featit/shared/work-area-seed-names';

/**
 * Whether a reader sent to the app's home (`/-/items`, All work areas' Work items) starts on Shared
 * with you instead (AGM-41, F-0621; F-0271 RL-5e). The app's home reads member work areas only, so
 * for a person who is a guest everywhere it would be an empty list.
 *
 * - A member of any work area stays on the app's home, whatever is shared with them.
 * - A person whose every listed work area is a guest's lands on Shared with you.
 * - A person with no work area listed lands there when something is shared with them (RL-5e).
 *
 * ⛔ Not `workAreas.length === 0` alone: since AGM-41 a guest's work areas are listed, so that test
 * stopped answering for anybody who is a guest somewhere.
 */
export function landsOnSharedWithYou({
  pathname,
  hasWorkAreaAnswer,
  workAreas,
  sharedWithYouCount,
}: {
  pathname: string;
  hasWorkAreaAnswer: boolean;
  workAreas: readonly (Pick<IProject, 'access'> & Partial<Pick<IProject, 'nameSeedId'>>)[];
  sharedWithYouCount: number | null;
}): boolean {
  if (pathname !== APP_HOME_PATH) return false;
  return isGuestOnlyReader({ hasWorkAreaAnswer, workAreas, sharedWithYouCount });
}

/**
 * Whether the reader is a guest everywhere: the rule above without the address, which the app's bar
 * also reads (`guestShellBar`). False until the work areas have answered, so nobody is given a guest's
 * bar for the moment before their list arrives.
 */
export function isGuestOnlyReader({
  hasWorkAreaAnswer,
  workAreas,
  sharedWithYouCount,
}: {
  hasWorkAreaAnswer: boolean;
  workAreas: readonly (Pick<IProject, 'access'> & Partial<Pick<IProject, 'nameSeedId'>>)[];
  sharedWithYouCount: number | null;
}): boolean {
  if (!hasWorkAreaAnswer) return false;
  /*
    F-0737 PW-R8: every account starts with a Personal work area, a guest's too. While it still
    carries its seed name it is the product's, not a sign the person works here, so it does not make a
    guest a member: they still land on Shared with you. Renamed, it is theirs, and counts.
  */
  const chosen = workAreas.filter(
    (workArea) => workArea.nameSeedId !== WORK_AREA_SEED_IDS.PERSONAL,
  );
  if (memberWorkAreas(chosen).length > 0) return false;
  return chosen.length > 0 || Boolean(sharedWithYouCount);
}
